// src/components/SpawnReceiver.jsx
// PD42h-1: coordinated surface spawning across paired devices.
// Subscribes to MSG.SURFACE_SPAWN. Dispatches to Electron IPC
// (window.hubDesktop.createSurface) when running in the desktop shell,
// otherwise falls back to window.open on a /present?surface=X&companion=1
// URL. Mounted globally under AuthGuard in App.jsx so every authenticated
// route participates.
//
// Dedup of cross-device relay echoes is deferred to PD42h-5.
// ASCII only. var only. ESM.

import { useEffect } from 'react';

// PD42h-5: dedup cache for SURFACE_SPAWN correlation_ids.
// Cross-device bus relay (PD42g-8) echoes messages in both directions,
// which without dedup causes the sending device to see its own spawn
// relayed back a fraction of a second later and dispatch again. We keep
// a 5-second rolling window of seen correlation_ids.
var DEDUP_TTL_MS = 5000;
var _seenCorrelations = {}; // id -> expires_at timestamp

function _pruneDedup(nowTs) {
  var cutoff = nowTs - DEDUP_TTL_MS;
  var keys = Object.keys(_seenCorrelations);
  for (var i = 0; i < keys.length; i++) {
    if (_seenCorrelations[keys[i]] < cutoff) delete _seenCorrelations[keys[i]];
  }
}

function _isDuplicate(correlationId) {
  if (!correlationId) return false;
  var now = Date.now();
  _pruneDedup(now);
  if (_seenCorrelations[correlationId]) return true;
  _seenCorrelations[correlationId] = now;
  return false;
}

// Test-only: reset the dedup cache. Used by PD42h-5 Playwright spec.
function _resetDedupCacheForTest() { _seenCorrelations = {}; }
if (typeof window !== 'undefined') {
  try { window.__pd42h_resetDedup = _resetDedupCacheForTest; } catch (e) {}
}

function isElectronHost() {
  try {
    return !!(typeof window !== 'undefined'
      && window.hubDesktop
      && window.hubDesktop.isElectron === true);
  } catch (e) { return false; }
}

function shouldHandle(targetDevice) {
  if (!targetDevice) return true;
  if (targetDevice === 'all') return true;
  if (targetDevice === 'this') return true;
  if (targetDevice === 'desktop') return isElectronHost();
  if (targetDevice === 'companion') return !isElectronHost();
  // 'paired' semantics require cross-device identity threading -- PD42h-5.
  return false;
}

function buildSurfaceUrl(surfaceId, companionHint) {
  var origin = '';
  try {
    if (typeof window !== 'undefined' && window.location && window.location.origin) {
      origin = window.location.origin;
    }
  } catch (e) {}
  var url = origin + '/present?surface=' + encodeURIComponent(surfaceId);
  if (companionHint) url += '&companion=1';
  return url;
}

function dispatch(payload) {
  if (!payload || !payload.surface_id) return;

  if (isElectronHost()
    && window.hubDesktop
    && typeof window.hubDesktop.createSurface === 'function') {
    var electronUrl = buildSurfaceUrl(payload.surface_id, false);
    try {
      window.hubDesktop.createSurface({
        surfaceId: payload.surface_id,
        url: electronUrl,
        title: 'Hub Surface'
      });
    } catch (e) {
      try { console.warn('[SpawnReceiver] createSurface failed', e); } catch (e2) {}
    }
    return;
  }

  // Browser / Vision Pro Safari fallback.
  var browserUrl = buildSurfaceUrl(payload.surface_id, true);
  try {
    window.open(browserUrl, '_blank');
  } catch (e) {
    try { console.warn('[SpawnReceiver] window.open failed', e); } catch (e2) {}
  }
}

export default function SpawnReceiver() {
  useEffect(function() {
    var unsub = null;
    var cancelled = false;

    var unsubVoice = null;
    import('/src/lib/spatial-bus.js').then(function(bus) {
      if (cancelled) return;
      // Idempotent init (PD42h-1): safe to call even if DailyView or
      // CompanionView has already initialized the bus on this tab.
      bus.init('spawn-receiver');
      unsub = bus.subscribe(bus.MSG.SURFACE_SPAWN, function(msg) {
        var payload = msg && msg.payload;
        if (!payload) return;
        // PD42h-5: drop cross-device relay echoes.
        if (_isDuplicate(payload.correlation_id)) return;
        if (!shouldHandle(payload.target_device)) return;
        dispatch(payload);
      });
      // PD42h-2: voice commands flow through DailyView's existing publish
      // to 'hub:voice_spatial_cmd'. When the payload is a spawn command
      // (type === 'spawn'), republish it as SURFACE_SPAWN so the receiver
      // above dispatches. Any other spatial command type falls through
      // unchanged to DailyView's own subscribers.
      unsubVoice = bus.subscribe('hub:voice_spatial_cmd', function(msg) {
        var cmd = msg && msg.payload;
        if (!cmd || cmd.type !== 'spawn' || !cmd.payload) return;
        var spawnPayload = cmd.payload;
        // PD42h-5: dedup voice bridge too. A voice command relayed from
        // the paired device must not re-trigger on arrival.
        if (_isDuplicate(spawnPayload.correlation_id)) return;
        // Republish for cross-device relay. BroadcastChannel does not
        // deliver to the sender's own tab, so also dispatch locally.
        bus.publish(bus.MSG.SURFACE_SPAWN, spawnPayload);
        if (shouldHandle(spawnPayload.target_device)) dispatch(spawnPayload);
      });
    }).catch(function() {});

    return function() {
      cancelled = true;
      if (unsub) { try { unsub(); } catch (e) {} }
      if (unsubVoice) { try { unsubVoice(); } catch (e) {} }
    };
  }, []);

  return null;
}
